import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { Grid, InfoCircleIcon, Wrapper } from '@farewill/ui'
import { useFormikContext } from 'formik'

import AddressInputWrapper from 'components/address'
import ContactBirthDate from 'components/contact-birth-date'
import CustomerNames from 'components/customer-names'
import { Checkbox } from 'components/form'
import Tooltip from 'components/tooltip'
import { Address } from 'lib/models/address'
import { FuneralPlanAttributes } from 'lib/models/funeral-plan'
import { fetchFuneralPlan } from 'state/actions'
import { strToBool } from 'utils/helpers'

type ContactDetailsProps = {
  disabled?: boolean
}

const ContactDetails = ({
  disabled,
}: ContactDetailsProps): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const { errors } = useSelector(
    (state: { errors: (string | { code: string; detail: string })[] }) => state
  )
  const { values, setFieldValue, handleSubmit } =
    useFormikContext<FuneralPlanAttributes>()

  // If the funeral plan has not been updated because the customer does not have an email address
  // reverse the checkbox click and submit the form again
  useEffect(() => {
    if (
      errors.find(
        (error) =>
          typeof error !== 'string' &&
          error.code === 'funeral_plan_not_updated_error'
      )
    ) {
      setFieldValue('isPostalService', 'true')
      handleSubmit()
    }
  }, [errors, handleSubmit, setFieldValue])

  return (
    <>
      <CustomerNames
        id={values?.purchaser?.id as number}
        firstName={values.purchaser?.firstName}
        lastName={values.purchaser?.lastName}
        fullLegalName={values.purchaser?.fullLegalName}
        preferredName={values.purchaser?.preferredName}
        pronouns={values.purchaser?.pronouns}
        disabled={disabled}
      />
      <Grid.Item spanFromL={6} margin={['M', 0, 0]}>
        <ContactBirthDate
          contactId={values?.purchaser?.id as number}
          dateOfBirth={values.purchaser?.dateOfBirth || null}
          highlightEmpty={!values.purchaser?.dateOfBirth}
        />
      </Grid.Item>
      <Grid.Item spanFromL={6} margin={['M', 0, 0]}>
        <AddressInputWrapper
          data={values.purchaser?.addresses?.[0] as Address}
          label="Address"
          name="purchaser.addresses"
          newAddressAttrs={{
            relatedResource: 'contact',
            relatedId: values.purchaser?.id as number,
            relation: 'addresses',
          }}
          warning={values.addressValidationWarning}
          // refetch the plan upon save so we retrieve the addressValidationWarning
          onSave={() => {
            dispatch(fetchFuneralPlan(funeralPlanId))
          }}
        />
        <Wrapper margin={['M', 0, 0]}>
          <Checkbox
            handleSave={() => handleSubmit()}
            label={
              <>
                <span>Customer requested postal service</span>
                <Tooltip content="Check this if the customer wants all communication to be sent via post">
                  <Wrapper
                    padding={[0, 0, 0, 'XS']}
                    as={InfoCircleIcon}
                    size="S"
                  />
                </Tooltip>
              </>
            }
            name="isPostalService"
            checked={strToBool(values.isPostalService)}
          />
        </Wrapper>
      </Grid.Item>
    </>
  )
}

export default ContactDetails
